/**
 * Emails admin tab — stub queue + preview + send + daily digest.
 *
 * Extracted from AdminApp.jsx (Feb 2026 modularisation phase 2). Two
 * components: `EmailsTab` (main list + send controls) and
 * `DailyDigestPanel` (VI Sales Head digest builder).
 */
import { useEffect, useState } from "react";
import { Eye, Send, Sparkles } from "lucide-react";
import api, { formatApiError } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { Loading, PageHeader, Err } from "./_shared";

// ---------------- Emails (Stub Queue + Preview) ----------------
export default function EmailsTab() {
  const [emails, setEmails] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [kind, setKind] = useState("cycle_launch");
  const [cycleId, setCycleId] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/emails").then((r) => setEmails(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => {
    api.get("/admin/cycles").then((r) => {
      setCycles(r.data);
      if (r.data[0]) setCycleId(r.data[0].id);
    });
    load();
  }, []);

  const doPreview = async () => {
    setErr("");
    try {
      const r = await api.post("/admin/emails/preview", { kind, cycle_id: cycleId });
      setPreview(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  const doSend = async () => {
    if (!cycleId) return;
    if (!window.confirm(`Enqueue ${kind} emails for this cycle? (STUB — no real SMTP, queues records with status='sent_simulated'.)`)) return;
    setSending(true); setErr("");
    try {
      const r = await api.post("/admin/emails/send", { kind, cycle_id: cycleId });
      alert(`Queued ${r.data.queued} emails · Skipped ${r.data.skipped}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (err) return <Err msg={err} />;
  if (!emails) return <Loading />;

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        eyebrow="EMAIL NOTIFICATIONS"
        title="Cycle notifications"
        subtitle="Compose and dispatch cycle-launch, reminder and report-ready emails. Currently in STUB mode — records are queued with status='sent_simulated'. Connect Resend or SendGrid in v2."
      />

      <div className="bg-[#FEF3C7] border-l-4 border-[#D97706] p-3 text-[12px] text-[#92400E]">
        <strong>STUB mode.</strong> No actual emails are dispatched. All queued records are visible below so VI can review the copy + recipient list before going live.
      </div>

      <div className="card p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="overline mb-1 block">Kind</label>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="email-kind">
            <option value="cycle_launch">Cycle launch</option>
            <option value="reminder">Reminder (non-starters)</option>
            <option value="report_ready">Report ready (scored)</option>
          </select>
        </div>
        <div>
          <label className="overline mb-1 block">Cycle</label>
          <select className="input" value={cycleId} onChange={(e) => setCycleId(e.target.value)} data-testid="email-cycle">
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={doPreview} className="btn btn-secondary" data-testid="email-preview"><Eye size={14} /> Preview</button>
        <button onClick={doSend} disabled={sending} className="btn btn-primary" data-testid="email-send"><Send size={14} /> {sending ? "Sending…" : "Send to cohort"}</button>
      </div>

      {preview && (
        <div className="card p-5">
          <div className="overline mb-2 !text-[#EE2737]">PREVIEW · TO {preview.recipient_email}</div>
          <div className="text-sm font-bold mb-2">From: {preview.sender}</div>
          <div className="text-sm font-bold mb-3 border-b border-[#E5E7EB] pb-2">Subject: {preview.subject}</div>
          <pre className="text-[12.5px] text-[#0A0A0A] font-body whitespace-pre-wrap leading-relaxed" data-testid="email-preview-body">{preview.body}</pre>
        </div>
      )}

      <DailyDigestPanel onSent={load} />

      <div>
        <div className="overline mb-2">SENT QUEUE · {emails.length} RECORD{emails.length !== 1 ? "S" : ""}</div>
        <div className="card overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>When</th><th>Kind</th><th>Recipient</th><th>Subject</th><th>Cycle</th><th>Status</th></tr></thead>
            <tbody>
              {emails.length === 0 && <tr><td colSpan="6" className="text-center text-[#878787] py-10 text-sm">No emails queued yet. Use the controls above to enqueue.</td></tr>}
              {emails.map((e) => (
                <tr key={e.id} data-testid={`email-row-${e.id}`}>
                  <td className="text-[11px] text-[#878787]">{(e.sent_at || e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td><span className="badge badge-neutral">{e.kind.replace("_", " ")}</span></td>
                  <td className="font-semibold">{e.recipient_name} <span className="text-[#878787] font-normal">· {e.recipient_email}</span></td>
                  <td className="text-[13px]">{e.subject}</td>
                  <td className="text-[12px] text-[#525252]">{e.cycle_name}</td>
                  <td><span className="badge badge-info">{e.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Shared widgets ----------------
function DailyDigestPanel({ onSent }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [recipient, setRecipient] = useState("salesleader@vodafoneidea.com");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const doPreview = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.post("/admin/digest/preview", { recipient_email: recipient });
      setPreview(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const doSend = async () => {
    if (!window.confirm(`Send today's Daily Pulse to ${recipient}? (STUB — queues with status='sent_simulated'.)`)) return;
    setSending(true); setErr("");
    try {
      await api.post("/admin/digest/send", { recipients: [recipient] });
      onSent?.();
      alert("Daily Pulse queued");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="card p-5 border-l-4 !border-[#EE2737]" data-testid="daily-digest-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="overline !text-[#EE2737]">VI SALES HEAD · DAILY PULSE</div>
          <h3 className="font-heading font-bold text-xl mt-1">One paragraph. Every morning.</h3>
          <p className="text-[12.5px] text-[#525252] max-w-2xl mt-1 leading-relaxed">
            An AI-generated executive briefing covering last-24h scored attempts, integrity flags awaiting review, and cohort competency momentum vs prior cycle. Designed for the Sales L&D Head who will not log in daily.
          </p>
        </div>
        <Sparkles size={28} className="text-[#EE2737]" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-4">
        <div className="md:col-span-2">
          <label className="overline mb-1 block">Recipient</label>
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="input"
            placeholder="salesleader@vodafoneidea.com"
            data-testid="digest-recipient"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={doPreview} disabled={loading} className="btn btn-secondary flex-1" data-testid="digest-preview">
            <Eye size={14} /> {loading ? "Drafting…" : "Preview"}
          </button>
          {isAdmin && (
            <button onClick={doSend} disabled={sending || !preview} className="btn btn-primary flex-1" data-testid="digest-send">
              <Send size={14} /> {sending ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>

      {err && <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-3 py-2 mb-3">{err}</div>}

      {preview && (
        <div className="bg-[#F8F9FA] border border-[#E5E7EB] p-4">
          <div className="text-[11px] text-[#878787] mb-2">From: {preview.sender} · To: {preview.recipient_email}</div>
          <div className="text-sm font-bold mb-3 border-b border-[#E5E7EB] pb-2">Subject: {preview.subject}</div>
          <pre className="text-[12.5px] text-[#0A0A0A] font-body whitespace-pre-wrap leading-relaxed mb-4" data-testid="digest-preview-body">{preview.body}</pre>

          <details className="text-[11px] text-[#525252]">
            <summary className="cursor-pointer font-bold uppercase tracking-wider text-[#878787]">Signals used to synthesise</summary>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="bg-white p-2 border border-[#E5E7EB]">
                <div className="overline">SCORED · 24H</div>
                <div className="font-heading font-black text-2xl">{preview.payload?.recent_scored_count ?? 0}</div>
              </div>
              <div className="bg-white p-2 border border-[#E5E7EB]">
                <div className="overline">PENDING ID OVERRIDE</div>
                <div className="font-heading font-black text-2xl">{preview.payload?.pending_overrides?.length ?? 0}</div>
              </div>
              <div className="bg-white p-2 border border-[#E5E7EB]">
                <div className="overline">INTEGRITY FLAGGED</div>
                <div className="font-heading font-black text-2xl">{preview.payload?.flagged?.length ?? 0}</div>
              </div>
            </div>
            {preview.payload?.movement?.length > 0 && (
              <div className="mt-3 bg-white p-2 border border-[#E5E7EB]">
                <div className="overline mb-1">COHORT MOVEMENT VS PRIOR CYCLE</div>
                <table className="text-[11px] w-full">
                  <tbody>
                    {preview.payload.movement.slice(0, 7).map((m) => (
                      <tr key={m.competency} className="border-b border-[#F1F1F1]">
                        <td className="py-1">{m.competency.replace(/_/g, " ")}</td>
                        <td className="py-1 text-right tabular-nums">{m.current}</td>
                        <td className="py-1 text-right tabular-nums w-16">
                          {m.delta === null ? "—" : (
                            <span className={m.delta > 0 ? "text-[#10B981]" : m.delta < 0 ? "text-[#EE2737]" : "text-[#878787]"}>
                              {m.delta > 0 ? "+" : ""}{m.delta}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        </div>
      )}

      {!isAdmin && (
        <div className="text-[11px] text-[#878787] mt-3">
          You can preview the daily pulse you'd receive. Only Mercury admins can dispatch.
        </div>
      )}
    </div>
  );
}
